import React, { useEffect, useMemo, useRef, useState } from 'react'
import useSWR from 'swr'
import { format } from 'date-fns'
import type { DateRange } from 'react-day-picker'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useToast } from '@/components/ui/use-toast'
import Loader from '@/components/ui/Loader'
import { postFetcher } from 'api/swr'
import { DateRangePicker } from 'components/ui/date-picker'
import Selector from 'components/ui/Selector'
import { RANKING_COUNTRY_OPTIONS } from './constants'

type ListedWord = {
  word: string
  category: string
  count: number
  rank: number
  havePv: number
  nullPv: number
}

type ResultRates = {
  country: string
  lang: string
  start: string
  end: string
  havePv: number
  nullPv: number
  pending: number
  positive: ListedWord[]
  blacklist: ListedWord[]
  rows: ListedWord[]
}

const LANG_LABEL: Record<string, string> = {
  en: '英语',
  ja: '日语',
  es: '西班牙语',
  pt: '葡萄牙语',
  de: '德语',
}

function shanghaiDay(offsetDays: number) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date())
  const year = Number(parts.find((part) => part.type === 'year')?.value)
  const month = Number(parts.find((part) => part.type === 'month')?.value)
  const day = Number(parts.find((part) => part.type === 'day')?.value)
  const utc = new Date(Date.UTC(year, month - 1, day + offsetDays))
  const y = utc.getUTCFullYear()
  const m = String(utc.getUTCMonth() + 1).padStart(2, '0')
  const d = String(utc.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function csvCell(value: string | number) {
  const text = String(value)
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`
  return text
}

function downloadWordCsv(data: ResultRates) {
  const header = ['搜索词', '排行', '搜索次数', '有结果次数', '无结果次数', '有结果率', '无结果率']
  const lines = data.rows.map((row) => {
    const total = row.havePv + row.nullPv
    return [
      row.word,
      row.rank,
      row.count,
      row.havePv,
      row.nullPv,
      formatRate(row.havePv, total),
      formatRate(row.nullPv, total),
    ].map(csvCell).join(',')
  })
  const csv = `\uFEFF${[header.join(','), ...lines].join('\n')}`
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `搜索词_${data.country}_${data.start}_${data.end}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

function dateFromDay(day: string) {
  const [year, month, date] = day.split('-').map(Number)
  return new Date(year, month - 1, date)
}

function formatRate(part: number, total: number) {
  if (total <= 0) return '—'
  return `${((100 * part) / total).toFixed(1)}%`
}

export default function ResultRateDialog({
  open,
  onOpenChange,
  initialCountry,
  initialDate,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialCountry: number
  initialDate: string
}) {
  const [countryCode, setCountryCode] = useState(initialCountry)
  const [range, setRange] = useState<DateRange | undefined>()
  const [query, setQuery] = useState<{
    country: string
    start: string
    end: string
  } | null>(null)
  const [addedKeys, setAddedKeys] = useState<Set<string>>(new Set())
  const [addingKey, setAddingKey] = useState('')
  const [addingAll, setAddingAll] = useState(false)
  const { toast } = useToast()
  const today = shanghaiDay(0)
  const openedRef = useRef(false)
  const country = useMemo(
    () => RANKING_COUNTRY_OPTIONS.find((item) => item.code === countryCode),
    [countryCode],
  )

  useEffect(() => {
    if (!open) {
      openedRef.current = false
      setQuery(null)
      return
    }
    if (openedRef.current || !initialDate) return
    openedRef.current = true
    const option = RANKING_COUNTRY_OPTIONS.find(
      (item) => item.code === initialCountry,
    )
    const day = initialDate
    const date = dateFromDay(day)
    setCountryCode(initialCountry)
    setRange({ from: date, to: date })
    if (!option?.short) return
    setAddedKeys(new Set())
    setQuery({ country: option.short, start: day, end: day })
  }, [initialCountry, initialDate, open])

  const start = range?.from ? format(range.from, 'yyyy-MM-dd') : ''
  const end = range?.to ? format(range.to, 'yyyy-MM-dd') : ''
  const { data, isValidating, error, mutate } = useSWR<ResultRates>(
    query
      ? ['/search-hot-words/result-rates', query]
      : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
    },
  )
  const loading = Boolean(query) && isValidating
  const ready =
    Boolean(query) &&
    data?.country === query?.country &&
    data.start === query.start &&
    data.end === query.end &&
    !loading
  const havePv = ready ? data.havePv : 0
  const nullPv = ready ? data.nullPv : 0
  const total = havePv + nullPv
  const errorMessage =
    query && !loading && error
      ? error instanceof Error
        ? error.message
        : String(error)
      : ''

  const confirm = () => {
    if (!country?.short || !start || !end || loading) return
    const next = { country: country.short, start, end }
    if (
      query?.country === next.country &&
      query.start === next.start &&
      query.end === next.end
    ) {
      void mutate()
      return
    }
    setQuery(next)
  }

  const exportWords = () => {
    if (!ready || !data) return
    downloadWordCsv(data)
  }

  const addToList = async (kind: 'positive' | 'blacklist', word: ListedWord) => {
    if (!data) return
    const key = `${kind}:${word.word}`
    if (addedKeys.has(key) || addingKey) return
    setAddingKey(key)
    try {
      const result = await postFetcher('/search-hot-words/list-words', {
        arg: {
          kind,
          country: data.country,
          word: word.word,
          category: word.category,
        },
      })
      setAddedKeys((current) => new Set(current).add(key))
      toast({
        title: result?.added ? '已加入' : '已在列表中',
        description: word.word,
      })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: '加入失败',
        description: error instanceof Error ? error.message : '没有写进列表',
      })
    } finally {
      setAddingKey('')
    }
  }

  const addAllBlacklist = async () => {
    if (!data || addingAll || addingKey) return
    const pending = data.blacklist.filter(
      (word) => !addedKeys.has(`blacklist:${word.word}`),
    )
    if (pending.length === 0) return
    setAddingAll(true)
    try {
      const result = await postFetcher('/search-hot-words/list-words/batch', {
        arg: {
          kind: 'blacklist',
          country: data.country,
          words: pending.map((word) => ({
            word: word.word,
            category: word.category,
          })),
        },
      })
      setAddedKeys((current) => {
        const next = new Set(current)
        for (const word of pending) next.add(`blacklist:${word.word}`)
        return next
      })
      toast({
        title: '已全部加入黑名单',
        description: `新加入 ${result?.added ?? pending.length} 个`,
      })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: '全部加入失败',
        description: error instanceof Error ? error.message : '没有写进黑名单',
      })
    } finally {
      setAddingAll(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>AI 分析</DialogTitle>
          <DialogDescription>
            按搜索次数计算。分母是有结果和无结果的次数，不含失败。
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <div
            className={`flex flex-wrap items-center gap-2 ${loading ? 'pointer-events-none opacity-60' : ''}`}
          >
          <span className="text-sm text-muted-foreground">国家</span>
          <Selector
            datas={RANKING_COUNTRY_OPTIONS}
            labelName="国家"
            value={countryCode}
            update={setCountryCode}
            showCode
            width="w-[220px]"
          />
          <span className="text-sm text-muted-foreground">时间</span>
          <DateRangePicker
            range={range}
            onSelect={setRange}
            disabled={(date) => format(date, 'yyyy-MM-dd') > today}
          />
          <Button
            type="button"
            onClick={confirm}
            disabled={loading || !country?.short || !start || !end}
          >
            {loading ? '分析中' : '确认'}
          </Button>
          </div>
          <Button
            type="button"
            variant="outline"
            className="ml-auto"
            disabled={!ready}
            onClick={exportWords}
          >
            导出
          </Button>
        </div>

        {!end ? (
          <div className="flex h-36 items-center justify-center text-sm text-muted-foreground">
            再点一次结束日期，然后点确认
          </div>
        ) : loading ? (
          <div className="flex h-36 flex-col items-center justify-center gap-2">
            <Loader />
            <p className="text-sm text-muted-foreground">
              正在把还没分过类的搜索词全部送给 AI
            </p>
          </div>
        ) : errorMessage ? (
          <div className="flex h-36 items-center justify-center text-sm text-muted-foreground">
            {errorMessage}
          </div>
        ) : ready && data ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <RateCard
                label="无结果搜索词占比"
                hint="所选时间段、当前国家的整体无结果比例"
                value={formatRate(nullPv, total)}
                tone="empty"
              />
              <RateCard
                label="有结果搜索词占比"
                hint="所选时间段、当前国家的整体有结果比例"
                value={formatRate(havePv, total)}
                tone="hit"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <WordList
                title="正向词"
                hint={`按${LANG_LABEL[data.lang] ?? data.lang}分类，搜索次数前 30`}
                words={data.positive}
                empty="这次没有正向词"
                categoryLabel="分类"
                rateLabel="有结果率"
                rate={(word) => formatRate(word.havePv, word.havePv + word.nullPv)}
                showRank
                actionLabel="加入正向词"
                addedKeys={addedKeys}
                addingKey={addingKey}
                actionKind="positive"
                onAction={(word) => addToList('positive', word)}
              />
              <WordList
                title="黑名单词"
                hint="命中风险分类的全部留下。已经在黑名单里的不再显示"
                words={data.blacklist.filter(
                  (word) => !addedKeys.has(`blacklist:${word.word}`),
                )}
                empty="没有待加入的黑名单词"
                categoryLabel="风险类型"
                rateLabel="无结果率"
                rate={(word) => formatRate(word.nullPv, word.havePv + word.nullPv)}
                actionLabel="加入黑名单"
                addedKeys={addedKeys}
                addingKey={addingAll ? 'blacklist:*' : addingKey}
                actionKind="blacklist"
                onAction={(word) => addToList('blacklist', word)}
                headerAction={{
                  label: addingAll ? '加入中' : '全部加入',
                  disabled:
                    addingAll ||
                    Boolean(addingKey) ||
                    data.blacklist.every((word) =>
                      addedKeys.has(`blacklist:${word.word}`),
                    ),
                  onClick: addAllBlacklist,
                }}
              />
            </div>
            {data.pending > 0 ? (
              <p className="text-center text-sm text-muted-foreground">
                有 {data.pending} 个词这次没有分成功。再点确认只会重试这些词。
              </p>
            ) : null}
          </div>
        ) : (
          <div className="flex h-36 items-center justify-center text-sm text-muted-foreground">
            选好国家和日期后，点确认再查询
          </div>
        )}
        {ready && total === 0 && !errorMessage ? (
          <p className="text-center text-sm text-muted-foreground">
            这段时间这个国家没有有结果或无结果的搜索
          </p>
        ) : null}
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            关闭
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function WordList({
  title,
  hint,
  words,
  empty,
  categoryLabel,
  rateLabel,
  rate,
  showRank,
  actionLabel,
  actionKind,
  addedKeys,
  addingKey,
  onAction,
  headerAction,
}: {
  title: string
  hint: string
  words: ListedWord[]
  empty: string
  categoryLabel: string
  rateLabel: string
  rate: (word: ListedWord) => string
  showRank?: boolean
  actionLabel?: string
  actionKind?: 'positive' | 'blacklist'
  addedKeys?: Set<string>
  addingKey?: string
  onAction?: (word: ListedWord) => void
  headerAction?: { label: string; disabled?: boolean; onClick: () => void }
}) {
  return (
    <section className="min-w-0 rounded-lg border">
      <div className="flex items-start justify-between gap-3 border-b px-3 py-2">
        <div>
          <div className="text-sm font-medium">{title}</div>
          <div className="text-xs text-muted-foreground">{hint}</div>
        </div>
        {headerAction ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            disabled={headerAction.disabled}
            onClick={headerAction.onClick}
          >
            {headerAction.label}
          </Button>
        ) : null}
      </div>
      {words.length === 0 ? (
        <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
          {empty}
        </div>
      ) : (
        <div className="max-h-80 overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background">
              <TableRow>
                <TableHead>搜索词</TableHead>
                <TableHead>{categoryLabel}</TableHead>
                <TableHead className="text-right">次数</TableHead>
                {showRank ? <TableHead className="text-right">排行</TableHead> : null}
                <TableHead className="text-right">{rateLabel}</TableHead>
                {onAction ? <TableHead className="text-right">操作</TableHead> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {words.map((word) => (
                <TableRow key={`${word.word}-${word.rank}`}>
                  <TableCell className="max-w-[140px] truncate font-medium">
                    {word.word}
                  </TableCell>
                  <TableCell>{word.category}</TableCell>
                  <TableCell className="text-right tabular-nums">{word.count}</TableCell>
                  {showRank ? (
                    <TableCell className="text-right tabular-nums">{word.rank}</TableCell>
                  ) : null}
                  <TableCell className="text-right tabular-nums">{rate(word)}</TableCell>
                  {onAction ? (
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={
                          addedKeys?.has(`${actionKind}:${word.word}`) ||
                          addingKey === `${actionKind}:${word.word}` ||
                          addingKey === `${actionKind}:*`
                        }
                        onClick={() => onAction(word)}
                      >
                        {addedKeys?.has(`${actionKind}:${word.word}`)
                          ? '已加入'
                          : actionLabel}
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  )
}

function RateCard({
  label,
  hint,
  value,
  tone,
}: {
  label: string
  hint: string
  value: string
  tone: 'empty' | 'hit'
}) {
  return (
    <div
      className={
        tone === 'empty'
          ? 'rounded-xl bg-orange-500/10 px-5 py-6'
          : 'rounded-xl bg-emerald-500/10 px-5 py-6'
      }
    >
      <div className="text-sm text-muted-foreground">{label}</div>
      <div
        className={
          tone === 'empty'
            ? 'mt-3 text-4xl font-semibold tabular-nums text-orange-600'
            : 'mt-3 text-4xl font-semibold tabular-nums text-emerald-600'
        }
      >
        {value}
      </div>
      <div className="mt-2 text-xs text-muted-foreground">{hint}</div>
    </div>
  )
}
